/**
 * Value Breakdown Chart
 * 
 * A D3-based grouped bar chart showing Value Generated vs Cost to Generate
 * across historical, current, and projected reporting periods.
 * 
 * Features:
 * - Side-by-side grouped bars (not stacked)
 * - Current period highlighting
 * - Projected periods shown as range bars (low to high) with increasing uncertainty
 * - Responsive sizing
 */

import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import styles from './ValueBreakdownChart.module.css';

export interface ChartPeriodData {
  periodId: string;
  periodName: string;
  startDate: string;
  endDate: string;
  isProjected: boolean;
  isCurrent: boolean;
  // For historical/current periods (point values)
  valueGenerated?: number;
  costToGenerate?: number;
  // For projected periods (ranges with uncertainty)
  valueGeneratedLow?: number;
  valueGeneratedHigh?: number;
  costToGenerateLow?: number;
  costToGenerateHigh?: number;
}

interface ValueBreakdownChartProps {
  /** Chart data for all periods */
  data: ChartPeriodData[];
  /** Chart height in pixels */
  height?: number;
  /** Color for value generated bars */
  valueColor?: string;
  /** Color for cost bars */
  costColor?: string;
  /** Label for value generated */
  valueLabel?: string;
  /** Label for cost */
  costLabel?: string;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export const ValueBreakdownChart: React.FC<ValueBreakdownChartProps> = ({
  data,
  height = 300,
  valueColor = '#10b981',
  costColor = '#ef4444',
  valueLabel = 'Value Generated',
  costLabel = 'Cost to Generate'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Calculate the max value for y-axis scale
  const maxValue = useMemo(() => {
    let max = 0;
    data.forEach(d => {
      if (d.isProjected) {
        max = Math.max(max, d.valueGeneratedHigh ?? 0, d.costToGenerateHigh ?? 0);
      } else {
        max = Math.max(max, d.valueGenerated ?? 0, d.costToGenerate ?? 0);
      }
    });
    return max * 1.1; // Add 10% padding
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || data.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);
    
    // ISSUE-058/066 fix: Use requestAnimationFrame to batch DOM reads and avoid forced reflows
    const animationFrameId = requestAnimationFrame(() => {
      if (!svgRef.current || !containerRef.current) return;
      
      // Clear previous content
      svg.selectAll('*').remove();

      // Dimensions
      const margin = { top: 20, right: 30, bottom: 60, left: 70 };
      const width = container.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      // Create main group
      const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X scale - band for periods
    const x0 = d3.scaleBand()
      .domain(data.map(d => d.periodId))
      .range([0, innerWidth])
      .padding(0.2);

    // X1 scale - for grouped bars within each period
    const x1 = d3.scaleBand()
      .domain(['value', 'cost'])
      .range([0, x0.bandwidth()])
      .padding(0.1);

    // Y scale
    const y = d3.scaleLinear()
      .domain([0, maxValue])
      .nice()
      .range([innerHeight, 0]);

    // Add X axis
    const xAxis = g.append('g')
      .attr('class', styles.axis)
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x0).tickFormat((d) => {
        const period = data.find(p => p.periodId === d);
        return period?.periodName ?? d;
      }));

    // Rotate x-axis labels if needed
    xAxis.selectAll('text')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em')
      .style('font-size', '11px');

    // Add Y axis with currency formatting
    g.append('g')
      .attr('class', styles.axis)
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => formatCurrency(d as number)));

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 15)
      .attr('x', -innerHeight / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'currentColor')
      .style('font-size', '12px')
      .text('Dollars');

    // Draw bars for each period
    data.forEach(period => {
      const periodG = g.append('g')
        .attr('transform', `translate(${x0(period.periodId)},0)`);

      // Background highlight for current period
      if (period.isCurrent) {
        periodG.append('rect')
          .attr('x', -x0.padding() * x0.step() / 2)
          .attr('y', 0)
          .attr('width', x0.bandwidth() + x0.padding() * x0.step())
          .attr('height', innerHeight)
          .attr('fill', '#fef3c7')
          .attr('rx', 4);
        
        // "Current" label
        periodG.append('text')
          .attr('x', x0.bandwidth() / 2)
          .attr('y', -5)
          .attr('text-anchor', 'middle')
          .attr('fill', '#92400e')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .text('CURRENT');
      }

      if (period.isProjected) {
        // Projected: Draw range bars (floating from low to high)
        const valueLow = period.valueGeneratedLow ?? 0;
        const valueHigh = period.valueGeneratedHigh ?? 0;
        const costLow = period.costToGenerateLow ?? 0;
        const costHigh = period.costToGenerateHigh ?? 0;

        // Value range bar
        const valueBarG = periodG.append('g');
        
        // Range bar background (lighter)
        valueBarG.append('rect')
          .attr('x', x1('value')!)
          .attr('y', y(valueHigh))
          .attr('width', x1.bandwidth())
          .attr('height', y(valueLow) - y(valueHigh))
          .attr('fill', valueColor)
          .attr('opacity', 0.4)
          .attr('rx', 2);

        // Range indicator lines
        valueBarG.append('line')
          .attr('x1', x1('value')!)
          .attr('x2', x1('value')! + x1.bandwidth())
          .attr('y1', y(valueHigh))
          .attr('y2', y(valueHigh))
          .attr('stroke', valueColor)
          .attr('stroke-width', 2);

        valueBarG.append('line')
          .attr('x1', x1('value')!)
          .attr('x2', x1('value')! + x1.bandwidth())
          .attr('y1', y(valueLow))
          .attr('y2', y(valueLow))
          .attr('stroke', valueColor)
          .attr('stroke-width', 2);

        // Pattern fill for projected bars
        valueBarG.append('rect')
          .attr('x', x1('value')!)
          .attr('y', y(valueHigh))
          .attr('width', x1.bandwidth())
          .attr('height', y(valueLow) - y(valueHigh))
          .attr('fill', 'url(#diagonal-stripe-value)')
          .attr('opacity', 0.3)
          .attr('rx', 2);

        // Cost range bar
        const costBarG = periodG.append('g');

        costBarG.append('rect')
          .attr('x', x1('cost')!)
          .attr('y', y(costHigh))
          .attr('width', x1.bandwidth())
          .attr('height', y(costLow) - y(costHigh))
          .attr('fill', costColor)
          .attr('opacity', 0.4)
          .attr('rx', 2);

        costBarG.append('line')
          .attr('x1', x1('cost')!)
          .attr('x2', x1('cost')! + x1.bandwidth())
          .attr('y1', y(costHigh))
          .attr('y2', y(costHigh))
          .attr('stroke', costColor)
          .attr('stroke-width', 2);

        costBarG.append('line')
          .attr('x1', x1('cost')!)
          .attr('x2', x1('cost')! + x1.bandwidth())
          .attr('y1', y(costLow))
          .attr('y2', y(costLow))
          .attr('stroke', costColor)
          .attr('stroke-width', 2);

        costBarG.append('rect')
          .attr('x', x1('cost')!)
          .attr('y', y(costHigh))
          .attr('width', x1.bandwidth())
          .attr('height', y(costLow) - y(costHigh))
          .attr('fill', 'url(#diagonal-stripe-cost)')
          .attr('opacity', 0.3)
          .attr('rx', 2);

      } else {
        // Historical/Current: Draw solid bars from 0
        const valueGen = period.valueGenerated ?? 0;
        const costGen = period.costToGenerate ?? 0;

        // Value Generated bar
        periodG.append('rect')
          .attr('x', x1('value')!)
          .attr('y', y(valueGen))
          .attr('width', x1.bandwidth())
          .attr('height', innerHeight - y(valueGen))
          .attr('fill', valueColor)
          .attr('rx', 2);

        // Cost to Generate bar
        periodG.append('rect')
          .attr('x', x1('cost')!)
          .attr('y', y(costGen))
          .attr('width', x1.bandwidth())
          .attr('height', innerHeight - y(costGen))
          .attr('fill', costColor)
          .attr('rx', 2);
      }
    });

    // Add pattern definitions for projected bars
    const defs = svg.append('defs');

    // Value pattern
    const valuePattern = defs.append('pattern')
      .attr('id', 'diagonal-stripe-value')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 8)
      .attr('height', 8);
    
    valuePattern.append('path')
      .attr('d', 'M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4')
      .attr('stroke', valueColor)
      .attr('stroke-width', 1.5);

    // Cost pattern
    const costPattern = defs.append('pattern')
      .attr('id', 'diagonal-stripe-cost')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 8)
      .attr('height', 8);
    
    costPattern.append('path')
      .attr('d', 'M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4')
      .attr('stroke', costColor)
      .attr('stroke-width', 1.5);
    }); // End of requestAnimationFrame
    
    // ISSUE-058/066 fix: Cleanup animation frame on unmount
    return () => cancelAnimationFrame(animationFrameId);
  }, [data, height, maxValue, valueColor, costColor]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current && containerRef.current) {
        // Trigger re-render by updating the SVG
        const event = new Event('resize');
        window.dispatchEvent(event);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className={styles.chartContainer} ref={containerRef}>
      <svg ref={svgRef} className={styles.chart} />
      
      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span 
            className={styles.legendColor} 
            style={{ backgroundColor: valueColor }}
          />
          <span className={styles.legendLabel}>{valueLabel}</span>
        </div>
        <div className={styles.legendItem}>
          <span 
            className={styles.legendColor} 
            style={{ backgroundColor: costColor }}
          />
          <span className={styles.legendLabel}>{costLabel}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendPattern}>
            <svg width="16" height="16">
              <defs>
                <pattern id="legend-pattern" patternUnits="userSpaceOnUse" width="4" height="4">
                  <path d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2" stroke="#6b7280" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="16" height="16" fill="url(#legend-pattern)" rx="2"/>
            </svg>
          </span>
          <span className={styles.legendLabel}>Projected Range</span>
        </div>
      </div>
    </div>
  );
};

export default ValueBreakdownChart;
